import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { setUserTimeZone, browserTimeZone, TIME_ZONE_MIRROR_KEY } from '../utils/dateTime';
import { preferencesApi, themesApi } from '../services/api';
import { activeDbSlug } from '../services/activeDatabase';
import { applyTheme, cacheTheme } from '../theme/themeRuntime';
import { useAuth } from './AuthContext';
import {
  DEBUG_HOVER_TOOLS, DEBUG_OVERLAY_TOOLS, SHOW_DEBUG_KEY, CSS_VARS_MODE_KEY,
  syncHoverState, syncOverlayTool, notifyDebugChanged,
} from '../utils/debugOverlay';

/**
 * PreferencesContext — THE single read/write path for user preferences.
 *
 * Server (AppSec_UserPreferences via /api/preferences) is the authority.
 * localStorage keeps two mirror roles only:
 *   1. Boot cache ('dawn.prefs.cache') — instant paint before hydration.
 *   2. Imperative mirrors (dawn.uiTheme, dawn.debug.*, ...) — legacy keys that
 *      non-React code (App.js boot appliers, debugOverlay.js sync fns) reads
 *      synchronously. The context writes INTO them; nothing else should.
 *
 * Resolution precedence (client-side, mirrors PreferenceResolver.cs):
 *   user+machine → user+db → user+global →
 *   default(role+db) → default(role) → default(db) → default(system) → catalog
 */

const CACHE_KEY = 'dawn.prefs.cache';
const LAST_USER_KEY = 'dawn.prefs.lastUserId';
const FLUSH_DEBOUNCE_MS = 1500;

// Role precedence — mirrors highestRole() in AuthContext.js
const ROLE_ORDER = ['SysAdmin', 'AppAdmin', 'PowerUser', 'User', 'Guest'];

// ── machineId (same key + create-if-absent semantics as DataTransferContext) ──
const genUUID = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
};
export const getMachineId = () => {
  let id = localStorage.getItem('dawn.machineId');
  if (!id) { id = genUUID(); localStorage.setItem('dawn.machineId', id); }
  return id;
};

// ── Legacy localStorage mirrors (imperative readers depend on these) ─────────
const debugMirrors = {};
DEBUG_HOVER_TOOLS.forEach(t => { debugMirrors[`debug.hover.${t.key}`] = t.lsKey; });
DEBUG_OVERLAY_TOOLS.forEach(t => { debugMirrors[`debug.overlay.${t.key}`] = t.lsKey; });

const DETECTED_TZ_KEY = 'display.timezoneDetected';

export const MIRROR_KEYS = {
  'display.theme': 'dawn.uiTheme',
  'display.timezone': TIME_ZONE_MIRROR_KEY,
  'display.uiZoom': 'dawn.uiZoom',
  'display.density': 'dawn.density',
  'display.motion': 'dawn.motion',
  'security.sessionTimeout': 'pmd-security.sessionTimeout',
  'debug.showFooterPanel': SHOW_DEBUG_KEY,
  'debug.logging': 'dawn.debugMode',
  'debug.cssVarsMode': CSS_VARS_MODE_KEY,
  ...debugMirrors,
};

// Dead keys from killed preferences — swept on migration, never imported.
const SWEEP_PREFIXES = ['pmd-locale.', 'pmd-autofill.', 'pmd-sub.'];
const SWEEP_KEYS = ['activityTracker.concurrencyLimit', 'pmd-security.screenRestrict'];

// ── Theme / zoom appliers (shared with App.js boot path + Footer) ─────────────
// Themes are database rows (GET api/themes). The provider keeps this module-level list
// current so the standalone applyThemeToDom() can resolve tokens without a hook; null
// means "not loaded yet", in which case the boot-cached theme stays on screen.
let knownThemes = null;
// A whole-app trial ({ key, label, tokens, scope, source }): wins over the saved theme on every
// re-apply (theme reloads, database switch, preference writes) until stopped, applied as a
// preference, or the page reloads. Never cached, so a reload always shows the saved theme.
// scope is where Apply writes it: 'global' or a numeric AppSec_Databases id. source is
// 'preferences' when the Display theme picker started it, so the page can resume editing it.
let activeTrial = null;
export const applyThemeToDom = (value) => {
  if (activeTrial) { applyTheme(activeTrial.key, activeTrial.tokens); return; }
  if (knownThemes == null) return;
  // A missing or retired selection falls back to the Standard row, then to the :root defaults.
  const usableTheme = t => t && t.status !== 'Retired' && t.tokens && Object.keys(t.tokens).length > 0;
  const picked = value ? knownThemes.find(t => t.themeKey === value) : null;
  const theme = usableTheme(picked) ? picked : knownThemes.find(t => t.themeKey === 'standard');
  const usable = usableTheme(theme);
  const key = usable ? theme.themeKey : 'standard';
  const tokens = usable ? theme.tokens : null;
  applyTheme(key, tokens);
  cacheTheme(key, tokens);
};
export const applyZoomToDom = (value) => {
  document.documentElement.style.zoom = (!value || value === '100%') ? '' : value;
};
// Density and animations are classes on <html>; STUI.css swaps spacing tokens / transitions.
const swapHtmlClass = (prefix, cls) => {
  const html = document.documentElement;
  [...html.classList].filter(c => c.startsWith(prefix)).forEach(c => html.classList.remove(c));
  if (cls) html.classList.add(cls);
};
export const applyDensityToDom = (value) => swapHtmlClass('dawn-density-', value === 'compact' ? 'dawn-density-compact' : null);
/** 'system' follows the OS reduce-motion setting; full / reduced / off are explicit. */
export const applyMotionToDom = (value) => {
  const v = ['full', 'reduced', 'off'].includes(value) ? value : 'system';
  swapHtmlClass('dawn-motion-', `dawn-motion-${v}`);
};

// ── Cached-pref reader for pre-hydration code paths (App.js routing) ─────────
// Reads the GLOBAL-resolution cache — per-DB overrides aren't applied here
// (routing runs before hydration; good enough for defaults).
export const getCachedPref = (prefKey) => {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return cache?.resolved?.[prefKey] ?? null;
  } catch { return null; }
};

/**
 * Landing path for a db slug, honoring the general.landingPage preference.
 * 'last' uses the per-slug last-visited page tracked by the slug layout.
 */
export const getLandingPath = (slug) => {
  if (!slug) return '/select-database';
  if (getCachedPref('general.landingPage') === 'last') {
    const last = localStorage.getItem(`dawn.lastPage.${slug}`);
    if (last && last !== 'home') return `/${slug}/${last}`;
  }
  return `/${slug}/`;
};

const PreferencesContext = createContext(null);

// Standard for All Databases is never stored (the API clears it too): it is what a user without a
// theme of their own already gets, and a stored row would outrank every database's admin default.
// Standard on one database is kept, which is how a user opts out of that database's default.
const isGlobalThemeRow = (r) => r.prefKey === 'display.theme' && r.databaseInstanceId == null && !r.machineId;
const unstoredStandard = (r) => (isGlobalThemeRow(r) && r.value === 'standard' ? { ...r, value: null } : r);

export const PreferencesProvider = ({ children }) => {
  const { currentUser, realUser, isImpersonating, isSysAdmin } = useAuth();

  // payload = { catalog, rows, defaults, resolved } (server shapes, camelCased by JSON)
  const [payload, setPayload] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [failSoft, setFailSoft] = useState(false);
  const [slugToDbId, setSlugToDbId] = useState({});
  const [themes, setThemes] = useState(null);   // null until GET api/themes lands
  const [trial, setTrial] = useState(activeTrial);

  const pendingWrites = useRef(new Map());   // dedupe key → row dto
  const flushTimer = useRef(null);
  const writingMirrors = useRef(false);      // loop guard for dawn-debug-changed echo
  const payloadRef = useRef(null);
  payloadRef.current = payload;

  const catalogByKey = useCallback((p = payloadRef.current) => {
    const map = {};
    (p?.catalog || []).forEach(c => { map[c.prefKey] = c; });
    return map;
  }, []);

  // ── Client-side resolution ──────────────────────────────────────────────
  const resolve = useCallback((prefKey, dbSlug = null) => {
    const p = payloadRef.current;
    if (!p) return getCachedPref(prefKey);
    const def = catalogByKey(p)[prefKey];
    if (!def) return null;

    const slug = (dbSlug ?? activeDbSlug()).toLowerCase();
    const dbId = slugToDbId[slug] ?? null;
    const machineId = getMachineId();
    const rows = p.rows || [];

    if (def.allowPerMachine) {
      const m = rows.find(r => r.prefKey === prefKey && r.machineId === machineId);
      if (m) return m.value;
    }
    if (def.allowPerDatabase && dbId != null) {
      const d = rows.find(r => r.prefKey === prefKey && r.databaseInstanceId === dbId);
      if (d) return d.value;
    }
    const g = rows.find(r => r.prefKey === prefKey && r.databaseInstanceId == null && !r.machineId);
    if (g) return g.value;

    // Admin default tiers
    const defaults = p.defaults || [];
    const roles = currentUser?.roles || {};
    const roleInDb = roles[slug] || null;
    const myHighest = ROLE_ORDER.find(o => Object.values(roles).includes(o)) || null;
    const tiers = [
      d => roleInDb && d.databaseInstanceId === dbId && d.role === roleInDb,
      d => myHighest && d.databaseInstanceId == null && d.role === myHighest,
      d => dbId != null && d.databaseInstanceId === dbId && d.role == null,
      d => d.databaseInstanceId == null && d.role == null,
    ];
    for (const match of tiers) {
      const hit = defaults.find(d => d.prefKey === prefKey && match(d));
      if (hit) return hit.value;
    }
    return def.defaultValue;
  }, [catalogByKey, slugToDbId, currentUser]);

  // ── Mirror + visual application ─────────────────────────────────────────
  const applyMirrorsAndVisuals = useCallback(() => {
    const p = payloadRef.current;
    if (!p) return;
    writingMirrors.current = true;
    try {
      Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
        const v = resolve(prefKey);
        if (v != null) localStorage.setItem(lsKey, v);
      });
      applyThemeToDom(resolve('display.theme'));
      applyZoomToDom(resolve('display.uiZoom'));
      applyDensityToDom(resolve('display.density'));
      applyMotionToDom(resolve('display.motion'));
      setUserTimeZone(resolve('display.timezone'));
      DEBUG_OVERLAY_TOOLS.forEach(t => syncOverlayTool(t.key, localStorage.getItem(t.lsKey) === 'true'));
      syncHoverState();
      window.dispatchEvent(new Event('dawn-theme-changed'));
      notifyDebugChanged();
    } finally {
      writingMirrors.current = false;
    }
  }, [resolve]);

  /** Update local rows state optimistically (matches server upsert/delete semantics). */
  const upsertLocalRow = useCallback((row) => {
    setPayload(prev => {
      if (!prev) return prev;
      const rows = [...(prev.rows || [])];
      const idx = rows.findIndex(r =>
        r.prefKey === row.prefKey &&
        (r.databaseInstanceId ?? null) === (row.databaseInstanceId ?? null) &&
        (r.machineId ?? null) === (row.machineId ?? null));
      if (row.value == null) {
        if (idx >= 0) rows.splice(idx, 1);       // null = scope-row deletion
      } else if (idx >= 0) rows[idx] = { ...rows[idx], value: row.value };
      else rows.push(row);
      return { ...prev, rows };
    });
  }, []);

  /**
   * The server deletes a written row whose value equals what its scope inherits, so admin and
   * catalog default changes keep reaching the user. Drop those rows locally too; resolve() lands
   * on the same value without them.
   */
  const dropPruned = useCallback((res, keep = () => true) => {
    (res?.data?.pruned || []).filter(keep).forEach(r => upsertLocalRow({ ...r, value: null }));
  }, [upsertLocalRow]);

  // ── Batched server writes ───────────────────────────────────────────────
  const targetUserId = () => currentUser?.id;

  const flush = useCallback(async () => {
    if (pendingWrites.current.size === 0) return;
    const rows = [...pendingWrites.current.values()];
    pendingWrites.current.clear();
    try {
      const res = await preferencesApi.save(rows, targetUserId());
      // A row written again since this flush started is newer than the server's prune.
      dropPruned(res, r => !pendingWrites.current.has(`${r.prefKey}|${r.databaseInstanceId ?? ''}|${r.machineId ?? ''}`));
    } catch (e) {
      // Put failed rows back so the next write retries them; mirrors untouched.
      rows.forEach(r => {
        const k = `${r.prefKey}|${r.databaseInstanceId ?? ''}|${r.machineId ?? ''}`;
        if (!pendingWrites.current.has(k)) pendingWrites.current.set(k, r);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const queueWrite = useCallback((row) => {
    const k = `${row.prefKey}|${row.databaseInstanceId ?? ''}|${row.machineId ?? ''}`;
    pendingWrites.current.set(k, row);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }, [flush]);

  /**
   * Write-through setter for immediate-write clients (Footer, Sidebar, Header…).
   * scope: 'global' (default) | 'db' | 'machine'.
   * While impersonating: applies locally/visually only — server persistence is
   * suppressed so an admin can't silently rewrite the target's rows. Only the
   * Preferences page Save (savePrefs) writes target rows deliberately.
   */
  const setPref = useCallback((prefKey, value, scope = 'global', dbSlugOverride = null) => {
    const def = catalogByKey()[prefKey];
    const row = { prefKey, databaseInstanceId: null, machineId: null, value: String(value) };
    if (scope === 'machine' && def?.allowPerMachine) row.machineId = getMachineId();
    if (scope === 'db' && def?.allowPerDatabase) {
      const slug = (dbSlugOverride ?? activeDbSlug()).toLowerCase();
      const dbId = slugToDbId[slug];
      if (dbId == null) return;                 // unknown db — refuse rather than mis-scope
      row.databaseInstanceId = dbId;
    }
    if (prefKey === 'display.theme' && activeTrial) { activeTrial = null; setTrial(null); }
    const stored = unstoredStandard(row);
    upsertLocalRow(stored);
    const mirror = MIRROR_KEYS[prefKey];
    if (mirror != null) {
      writingMirrors.current = true;
      try { localStorage.setItem(mirror, row.value); } finally { writingMirrors.current = false; }
    }
    if (!isImpersonating && !failSoft) queueWrite(stored);
  }, [catalogByKey, slugToDbId, upsertLocalRow, queueWrite, isImpersonating, failSoft]);

  /**
   * Explicit batch save — the Preferences page Save button and the footer trial Apply. Throws on 400.
   * A theme saved for All Databases also deletes the per-database theme rows, so "All" means
   * every database rather than every database without its own theme.
   */
  const savePrefs = useCallback(async (rows) => {
    const globalTheme = rows.some(r => isGlobalThemeRow(r) && r.value != null);
    const batch = rows.map(unstoredStandard);
    if (globalTheme) {
      (payloadRef.current?.rows || [])
        .filter(r => r.prefKey === 'display.theme' && r.databaseInstanceId != null)
        .filter(r => !batch.some(b => b.prefKey === 'display.theme' && b.databaseInstanceId === r.databaseInstanceId))
        .forEach(r => batch.push({ prefKey: 'display.theme', databaseInstanceId: r.databaseInstanceId, machineId: null, value: null }));
    }
    const res = await preferencesApi.save(batch, targetUserId());
    if (activeTrial && batch.some(r => r.prefKey === 'display.theme')) { activeTrial = null; setTrial(null); }
    batch.forEach(upsertLocalRow);
    dropPruned(res);
    applyMirrorsAndVisuals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertLocalRow, dropPruned, applyMirrorsAndVisuals, currentUser]);

  const restoreDefaults = useCallback(async (dbId = null) => {
    await preferencesApi.restore(targetUserId(), dbId);
    setPayload(prev => prev ? {
      ...prev,
      rows: (prev.rows || []).filter(r =>
        r.prefKey === 'system.migrated' || (dbId != null && r.databaseInstanceId !== dbId)),
    } : prev);
    setTimeout(applyMirrorsAndVisuals, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyMirrorsAndVisuals, currentUser]);

  // ── Legacy localStorage migration (one-time, guarded) ───────────────────
  const runLegacyMigration = useCallback(async (freshPayload, userId, userIsSysAdmin) => {
    const rows = freshPayload.rows || [];
    const alreadyMigrated = rows.some(r => r.prefKey === 'system.migrated' && r.value === 'true');
    if (rows.length > 0 || alreadyMigrated) return false;

    const lastUser = localStorage.getItem(LAST_USER_KEY);
    if (lastUser && String(lastUser) !== String(userId)) return false;   // someone else's localStorage

    const cat = {};
    (freshPayload.catalog || []).forEach(c => { cat[c.prefKey] = c; });
    const batch = [];
    const addIfDiffers = (prefKey, value, extra = {}) => {
      const def = cat[prefKey];
      if (!def || value == null || value === '' || value === def.defaultValue) return;
      batch.push({ prefKey, databaseInstanceId: null, machineId: null, value: String(value), ...extra });
    };

    addIfDiffers('display.theme', localStorage.getItem('dawn.uiTheme'));
    addIfDiffers('display.uiZoom', localStorage.getItem('dawn.uiZoom'), { machineId: getMachineId() });
    addIfDiffers('general.defaultDatabase', localStorage.getItem('pmd-selected-db-slug'));
    addIfDiffers('downloads.clearMode', localStorage.getItem('dataTransfer.clearMode'));

    // Session timeout: clamp to the caller's legal options — a stored 'never'
    // on a non-admin must not 400 the whole batch.
    let timeout = localStorage.getItem('pmd-security.sessionTimeout');
    if (timeout && !userIsSysAdmin && (timeout === 'never' || parseInt(timeout, 10) > 120)) timeout = null;
    addIfDiffers('security.sessionTimeout', timeout);

    Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
      if (!prefKey.startsWith('debug.')) return;
      addIfDiffers(prefKey, localStorage.getItem(lsKey));
    });

    try {
      const nav = {
        expanded: JSON.parse(localStorage.getItem('pmd-nav-expanded') || '[]'),
        collapsed: localStorage.getItem('pmd-nav-collapsed') === 'true',
      };
      if (nav.expanded.length > 0 || nav.collapsed)
        batch.push({ prefKey: 'general.sidebarNavState', databaseInstanceId: null, machineId: null, value: JSON.stringify(nav) });
    } catch { /* unparseable legacy nav state — skip */ }

    batch.push({ prefKey: 'system.migrated', databaseInstanceId: null, machineId: null, value: 'true' });

    try {
      await preferencesApi.save(batch, userId);
      // Sweep keys belonging to killed preferences
      Object.keys(localStorage)
        .filter(k => SWEEP_PREFIXES.some(p => k.startsWith(p)) || SWEEP_KEYS.includes(k))
        .forEach(k => localStorage.removeItem(k));
      return true;
    } catch { return false; }
  }, []);

  // ── Hydration — driven by the auth payload (GetMe / ResolveAll carry it) ──
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!currentUser?.id) { setHydrated(false); setPayload(null); return; }

      let p = currentUser.preferences ?? null;
      if (!p) {
        try {
          const res = await preferencesApi.get(isImpersonating || !realUser ? currentUser.id : undefined);
          p = res.data;
        } catch { p = null; }
      }
      if (cancelled) return;

      if (!p || !p.catalog) {
        // Fail-soft: no tables / old API / resolver error. Cache + defaults only.
        setFailSoft(true);
        setHydrated(true);
        return;
      }
      setFailSoft(false);

      // Slug → numeric AppSec_Databases.ID map (per-DB scope writes need the
      // numeric id; the payload carries it because /api/databases/available
      // exposes DatabaseName as 'id', not the numeric key). Keyed by both the
      // real slug and lowercased display name — both conventions exist in-app.
      const map = {};
      (p.databases || []).forEach(d => {
        if (d.slug) map[d.slug.toLowerCase()] = d.id;
        if (d.displayName) map[d.displayName.toLowerCase()] = d.id;
      });
      setSlugToDbId(map);

      // One-time legacy import (self only — never while impersonating)
      if (!isImpersonating) {
        const migrated = await runLegacyMigration(p, currentUser.id, !!isSysAdmin);
        if (migrated) {
          try { p = (await preferencesApi.get()).data; } catch { /* keep pre-migration payload */ }
        }
        localStorage.setItem(LAST_USER_KEY, String(currentUser.id));
      }
      if (cancelled) return;

      setPayload(p);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ userId: currentUser.id, resolved: p.resolved || {} }));
      } catch { /* quota — cache is best-effort */ }
      setHydrated(true);
    };
    hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isImpersonating]);

  // The browser's zone, recorded so server-made output (subscription PDFs and emails) can use the
  // person's clock when their Timezone preference is 'auto'. Written only when it changes, never
  // while impersonating, and only when the catalog has the key.
  useEffect(() => {
    if (!hydrated || isImpersonating || failSoft || !payloadRef.current || !catalogByKey()[DETECTED_TZ_KEY]) return;
    const zone = browserTimeZone();
    if (zone && resolve(DETECTED_TZ_KEY) !== zone) setPref(DETECTED_TZ_KEY, zone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, isImpersonating, failSoft, catalogByKey]);

  // ── Themes (AppCfg_Themes via /api/themes) ───────────────────────────────
  // Non-SysAdmins receive Active themes only; SysAdmins also get Draft and Retired.
  const refreshThemes = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      // Always send the acting user: with auth on the API ignores it and uses the token; with
      // auth off it is the only way the API knows the caller is a SysAdmin (who also sees Drafts).
      const res = await themesApi.list(currentUser?.id);
      const list = Array.isArray(res.data) ? res.data : [];
      knownThemes = list;
      setThemes(list);
    } catch { /* keep whatever is applied (boot cache) when the API is unreachable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isImpersonating]);

  useEffect(() => { refreshThemes(); }, [refreshThemes]);

  // Re-apply the saved theme whenever the theme list (re)loads, e.g. after the builder saves
  useEffect(() => {
    if (themes != null && payloadRef.current) applyThemeToDom(resolve('display.theme'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes]);

  /** Try a theme on the whole app with unsaved tokens. reapplyVisuals() restores the saved one. */
  const previewThemeTokens = useCallback((key, tokens) => { applyTheme(key, tokens); }, []);

  /** Wear a theme app-wide without saving it; calling again updates it (live builder edits). */
  const startTrial = useCallback(({ key, label, tokens, scope = 'global', source = 'toolbar' }) => {
    activeTrial = { key, label: label || key, tokens, scope, source };
    setTrial(activeTrial);
    applyTheme(key, tokens);
    window.dispatchEvent(new Event('dawn-theme-changed'));
  }, []);

  /** Re-point where Apply will write the running trial ('global' or a database id). */
  const setTrialScope = useCallback((scope) => {
    if (!activeTrial) return;
    activeTrial = { ...activeTrial, scope };
    setTrial(activeTrial);
  }, []);

  const stopTrial = useCallback(() => {
    if (!activeTrial) return;
    activeTrial = null;
    setTrial(null);
    applyMirrorsAndVisuals();
  }, [applyMirrorsAndVisuals]);

  /** Keep the tried theme at the trial's scope (saving ends the trial). Saves the theme only. */
  const applyTrial = useCallback(async () => {
    if (!activeTrial) return;
    const { key, scope } = activeTrial;
    await savePrefs([{
      prefKey: 'display.theme', databaseInstanceId: typeof scope === 'number' ? scope : null, machineId: null, value: key,
    }]);
  }, [savePrefs]);

  // Apply mirrors + visuals whenever the payload lands or the identity swaps
  useEffect(() => {
    if (payload) applyMirrorsAndVisuals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload === null ? 0 : payload.rows, currentUser?.id]);

  // Re-resolve per-DB prefs (theme) when the database changes
  useEffect(() => {
    const onDbChanged = () => applyMirrorsAndVisuals();
    window.addEventListener('pmd-database-changed', onDbChanged);
    return () => window.removeEventListener('pmd-database-changed', onDbChanged);
  }, [applyMirrorsAndVisuals]);

  // Reverse mirror sync: overlays can self-write (classInventory panel close).
  // Diff mirrors against effective values and persist genuine changes.
  useEffect(() => {
    const onDebugChanged = () => {
      if (writingMirrors.current || !payloadRef.current) return;
      Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
        if (!prefKey.startsWith('debug.')) return;
        const mirror = localStorage.getItem(lsKey);
        if (mirror != null && mirror !== resolve(prefKey)) setPref(prefKey, mirror);
      });
    };
    window.addEventListener('dawn-debug-changed', onDebugChanged);
    return () => window.removeEventListener('dawn-debug-changed', onDebugChanged);
  }, [resolve, setPref]);

  // Flush pending writes on unload
  useEffect(() => {
    const onUnload = () => { if (flushTimer.current) clearTimeout(flushTimer.current); flush(); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [flush]);

  const value = {
    hydrated,
    failSoft,
    catalog: payload?.catalog || [],
    rows: payload?.rows || [],
    slugToDbId,
    getPref: resolve,
    setPref,
    savePrefs,
    restoreDefaults,
    adminRestore: (userId, dbId = null) => preferencesApi.restore(userId, dbId),
    reapplyVisuals: applyMirrorsAndVisuals,
    themes: themes || [],
    themesLoaded: themes != null,
    refreshThemes,
    previewThemeTokens,
    trial,
    startTrial,
    setTrialScope,
    stopTrial,
    applyTrial,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
};

/** Convenience hook: const [theme, setTheme] = usePreference('display.theme'); */
export const usePreference = (prefKey, scope = 'global') => {
  const { getPref, setPref } = usePreferences();
  const value = getPref(prefKey);
  const setter = useCallback(v => setPref(prefKey, v, scope), [prefKey, scope, setPref]);
  return [value, setter];
};
